import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import api, { petAPI } from '../../services/api';
import { showSuccessToast, showErrorToast } from '../../utils/notificationUtils';
import PhoneInputWithCountryCode from '../common/PhoneInputWithCountryCode';
import './NewPatientWizardModal.css';

/**
 * Multi-step wizard for creating a new pet guardian + pet.
 *
 * Step 1: Guardian info  (first name, last name, email, phone)
 * Step 2: Pet info       (name, species, breed, gender)
 *
 * On submit, calls POST /api/v1/pet-owner-medical-account-register
 * which creates both user + pet and sends a welcome email.
 *
 * Props:
 *   isOpen      - boolean
 *   onClose     - () => void
 *   visitType   - 'inpatient' | 'outpatient'  (for display only)
 *   onComplete  - (user, pet) => void  — called after successful registration
 */
const NewPatientWizardModal = ({
  isOpen,
  onClose,
  visitType = 'inpatient',
  onComplete,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  const [step, setStep] = useState(1);
  const [stepDirection, setStepDirection] = useState('forward');
  const [errors, setErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Guardian fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Pet fields
  const [petName, setPetName] = useState('');
  const [speciesId, setSpeciesId] = useState('');
  const [breedId, setBreedId] = useState('');
  const [petGender, setPetGender] = useState('');

  // Dropdown data
  const [speciesList, setSpeciesList] = useState([]);
  const [breedList, setBreedList] = useState([]);

  const scrollLockRef = useRef(0);

  // Reset everything when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setStepDirection('forward');
      setErrors([]);
      setIsSubmitting(false);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setPetName('');
      setSpeciesId('');
      setBreedId('');
      setPetGender('');
      setBreedList([]);

      // Fetch species
      petAPI.getPetsBySpecies(null, { dropdown: true })
        .then((res) => {
          setSpeciesList(Array.isArray(res.data) ? res.data : res.data?.data || []);
        })
        .catch(() => setSpeciesList([]));
    }
  }, [isOpen]);

  // Fetch breeds when species changes
  useEffect(() => {
    if (speciesId) {
      petAPI.getBreedBySpecies(speciesId, { include_all: true, dropdown: true })
        .then((res) => {
          setBreedList(Array.isArray(res.data) ? res.data : res.data?.data || []);
        })
        .catch(() => setBreedList([]));
    } else {
      setBreedList([]);
      setBreedId('');
    }
  }, [speciesId]);

  // Scroll lock
  useEffect(() => {
    if (!isOpen) return;
    scrollLockRef.current = window.scrollY || window.pageYOffset || 0;
    const originalStyle = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollLockRef.current}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = originalStyle.overflow;
      document.body.style.position = originalStyle.position;
      document.body.style.top = originalStyle.top;
      document.body.style.width = originalStyle.width;
      window.scrollTo(0, scrollLockRef.current || 0);
    };
  }, [isOpen]);

  // Step 1 validation
  const validateStep1 = () => {
    const errs = [];
    if (!firstName.trim()) errs.push(t('newPatientWizard.errors.firstNameRequired', 'First name is required'));
    if (!lastName.trim()) errs.push(t('newPatientWizard.errors.lastNameRequired', 'Last name is required'));
    if (!email.trim()) {
      errs.push(t('newPatientWizard.errors.emailRequired', 'Email is required'));
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        errs.push(t('newPatientWizard.errors.emailInvalid', 'Please enter a valid email'));
      }
    }
    if (!phone.trim()) {
      errs.push(t('newPatientWizard.errors.phoneRequired', 'Phone number is required'));
    } else if (phone.length < 10) {
      errs.push(t('newPatientWizard.errors.phoneInvalid', 'Please enter a valid phone number'));
    }
    return errs;
  };

  // Step 2 validation
  const validateStep2 = () => {
    const errs = [];
    if (!petName.trim()) errs.push(t('newPatientWizard.errors.petNameRequired', 'Pet name is required'));
    if (!speciesId) errs.push(t('newPatientWizard.errors.speciesRequired', 'Species is required'));
    if (!breedId) errs.push(t('newPatientWizard.errors.breedRequired', 'Breed is required'));
    return errs;
  };

  const handleNext = () => {
    const errs = validateStep1();
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors([]);
    setStepDirection('forward');
    setStep(2);
  };

  const handleBack = () => {
    setErrors([]);
    setStepDirection('back');
    setStep(1);
  };

  const handleSubmit = async () => {
    const errs = validateStep2();
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors([]);
    setIsSubmitting(true);

    try {
      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        pet_name: petName.trim(),
        pet_species: parseInt(speciesId, 10),
        pet_breed: parseInt(breedId, 10),
        pet_gender: petGender || undefined,
      };

      const res = await api.post('/api/v1/pet-owner-medical-account-register', payload);

      const responseData = res.data?.data || res.data;
      const createdUser = responseData?.user;
      const createdPet = responseData?.pet;

      showSuccessToast(
        t('newPatientWizard.success', 'Pet Guardian registered! A welcome email has been sent.')
      );

      onClose();

      if (onComplete && createdUser && createdPet) {
        // Format user to match guardians search response shape
        const formattedUser = {
          id: createdUser.id,
          first_name: createdUser.first_name,
          last_name: createdUser.last_name,
          email: createdUser.email,
          display_name: `${createdUser.first_name} ${createdUser.last_name}`.trim(),
          full_name: `${createdUser.first_name} ${createdUser.last_name}`.trim(),
          pets: {
            list: [createdPet],
          },
        };

        onComplete(formattedUser, createdPet);
      }
    } catch (error) {
      console.error('Error registering new patient:', error);
      if (error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;
        setErrors(Object.values(apiErrors).flat());
      } else if (error.response?.data?.message) {
        setErrors([error.response.data.message]);
      } else {
        setErrors([t('newPatientWizard.errors.generic', 'An error occurred. Please try again.')]);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="new-patient-wizard-overlay"
      onClick={onClose}
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <div
        className="new-patient-wizard-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="new-patient-wizard-header">
          <h2>
            <span aria-hidden="true">📋</span>
            {t('newPatientWizard.title', 'Register New Patient')}
          </h2>
          <button
            className="new-patient-wizard-close"
            onClick={onClose}
            disabled={isSubmitting}
          >
            ✕
          </button>
        </div>

        {/* Step indicator */}
        <div className="wizard-steps-indicator">
          <div className="wizard-step-dot">
            <span className={`wizard-step-number ${step === 1 ? 'active' : 'completed'}`}>
              {step > 1 ? '✓' : '1'}
            </span>
            <span className={`wizard-step-label ${step === 1 ? 'active' : 'completed'}`}>
              {t('newPatientWizard.step1.title', 'Pet Guardian')}
            </span>
          </div>

          <div className={`wizard-step-connector ${step > 1 ? 'completed' : ''}`} />

          <div className="wizard-step-dot">
            <span className={`wizard-step-number ${step === 2 ? 'active' : 'inactive'}`}>
              2
            </span>
            <span className={`wizard-step-label ${step === 2 ? 'active' : ''}`}>
              {t('newPatientWizard.step2.title', 'Pet Information')}
            </span>
          </div>
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div style={{ padding: '0 32px' }}>
            <div className="wizard-errors">
              {errors.map((err, i) => (
                <p key={i}>⚠️ {err}</p>
              ))}
            </div>
          </div>
        )}

        {/* Form body */}
        <div className="wizard-form-body">
          {step === 1 && (
            <div className={stepDirection === 'back' ? 'wizard-step-back' : ''} key="step1">
              <div className="wizard-form-grid">
                <div className="wizard-field">
                  <label>
                    <span>👤</span>
                    {t('newPatientWizard.fields.firstName', 'First Name')}
                    <span className="field-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t('newPatientWizard.placeholders.firstName', 'Enter first name')}
                    disabled={isSubmitting}
                    autoFocus
                  />
                </div>

                <div className="wizard-field">
                  <label>
                    <span>👤</span>
                    {t('newPatientWizard.fields.lastName', 'Last Name')}
                    <span className="field-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={t('newPatientWizard.placeholders.lastName', 'Enter last name')}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="wizard-field">
                  <label>
                    <span>📧</span>
                    {t('newPatientWizard.fields.email', 'Email')}
                    <span className="field-required">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('newPatientWizard.placeholders.email', 'name@example.com')}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="wizard-field">
                  <label>
                    <span>📱</span>
                    {t('newPatientWizard.fields.phone', 'Phone')}
                    <span className="field-required">*</span>
                  </label>
                  <PhoneInputWithCountryCode
                    value={phone}
                    onChange={setPhone}
                    disabled={isSubmitting}
                    placeholder="1234567890"
                    defaultCountryCode="+962"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className={stepDirection === 'forward' ? 'wizard-step-forward' : ''} key="step2">
              <div className="wizard-form-grid">
                <div className="wizard-field wizard-field-full">
                  <label>
                    <span>🐾</span>
                    {t('newPatientWizard.fields.petName', 'Pet Name')}
                    <span className="field-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={petName}
                    onChange={(e) => setPetName(e.target.value)}
                    placeholder={t('newPatientWizard.placeholders.petName', 'Enter pet name')}
                    disabled={isSubmitting}
                    autoFocus
                  />
                </div>

                <div className="wizard-field">
                  <label>
                    <span>🦴</span>
                    {t('newPatientWizard.fields.species', 'Species')}
                    <span className="field-required">*</span>
                  </label>
                  <select
                    value={speciesId}
                    onChange={(e) => {
                      setSpeciesId(e.target.value);
                      setBreedId('');
                    }}
                    disabled={isSubmitting}
                  >
                    <option value="" disabled>
                      {t('newPatientWizard.placeholders.species', 'Select species')}
                    </option>
                    {speciesList.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div className="wizard-field">
                  <label>
                    <span>🏷️</span>
                    {t('newPatientWizard.fields.breed', 'Breed')}
                    <span className="field-required">*</span>
                  </label>
                  <select
                    value={breedId}
                    onChange={(e) => setBreedId(e.target.value)}
                    disabled={isSubmitting || !speciesId}
                  >
                    <option value="" disabled>
                      {speciesId
                        ? t('newPatientWizard.placeholders.breed', 'Select breed')
                        : t('newPatientWizard.placeholders.breedDisabled', 'Select species first')
                      }
                    </option>
                    {breedList.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>

                <div className="wizard-field wizard-field-full">
                  <label>
                    <span>⚧</span>
                    {t('newPatientWizard.fields.gender', 'Gender')}
                    <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 400 }}>
                      ({t('common.optional', 'Optional')})
                    </span>
                  </label>
                  <div className="wizard-gender-group">
                    <label
                      className={`wizard-gender-option ${petGender === 'male' ? 'selected' : ''}`}
                      onClick={() => setPetGender('male')}
                    >
                      <input type="radio" name="petGender" value="male" />
                      ♂️ {t('newPatientWizard.genderOptions.male', 'Male')}
                    </label>
                    <label
                      className={`wizard-gender-option ${petGender === 'female' ? 'selected' : ''}`}
                      onClick={() => setPetGender('female')}
                    >
                      <input type="radio" name="petGender" value="female" />
                      ♀️ {t('newPatientWizard.genderOptions.female', 'Female')}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="wizard-form-footer">
          {step === 1 ? (
            <>
              <button
                type="button"
                className="wizard-btn wizard-btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                className="wizard-btn wizard-btn-primary"
                onClick={handleNext}
                disabled={isSubmitting}
              >
                {t('newPatientWizard.buttons.next', 'Next')} →
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="wizard-btn wizard-btn-secondary"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                ← {t('newPatientWizard.buttons.back', 'Back')}
              </button>
              <button
                type="button"
                className="wizard-btn wizard-btn-success"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="wizard-spinner" />
                    {t('newPatientWizard.buttons.submitting', 'Registering...')}
                  </>
                ) : (
                  <>
                    ✓ {t('newPatientWizard.buttons.submit', 'Register & Continue')}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NewPatientWizardModal;
